import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RecipeCompositionArea from "@/components/RecipeCompositionArea";
import CraftMarginSlider from "@/components/CraftMarginSlider";
import MarginSliderInput from "@/components/MarginSliderInput";
import { Toaster } from "@/components/ui/sonner";
import { ChefHat, DollarSign, Camera, FileText, Award, Building2, TrendingUp, AlertCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { normalizeDecimalInput } from "@/lib/numberUtils";
import imageCompression from 'browser-image-compression';
import usePageTitle from "@/hooks/usePageTitle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DEPARTMENTS = [
  { value: "Kitchen", label: "Kitchen", emoji: "👨‍🍳" },
  { value: "Bar", label: "Bar", emoji: "🍸" },
  { value: "Lab", label: "Lab (Brewery/Fermentation)", emoji: "🧪" },
  { value: "Bakery", label: "Bakery", emoji: "🥖" },
  { value: "Pastry", label: "Pastry/Desserts", emoji: "🍰" }
];

const CATEGORIES = [
  { value: "Appetizers", label: "Appetizers", emoji: "🥗" },
  { value: "Main Course", label: "Main Course", emoji: "🍽️" },
  { value: "Beverages", label: "Beverages", emoji: "🍹" },
  { value: "Desserts", label: "Desserts", emoji: "🍰" },
  { value: "Sides", label: "Sides", emoji: "🍟" },
  { value: "Soups", label: "Soups", emoji: "🍜" },
  { value: "Salads", label: "Salads", emoji: "🥗" },
  { value: "Breads", label: "Breads", emoji: "🥖" },
  { value: "Other", label: "Other", emoji: "🍴" }
];

const RecipeComposer = ({ settings }) => {
  usePageTitle("pageTitles.recipeComposer");
  const { t } = useTranslation();
  const [recipeName, setRecipeName] = useState("");
  const [category, setCategory] = useState("Main Course");
  const [department, setDepartment] = useState("Kitchen");
  const [menuPrice, setMenuPrice] = useState("");
  const [targetMargin, setTargetMargin] = useState(70); // Default 70% margin
  const [wastePercentage, setWastePercentage] = useState(5); // Default 5% waste
  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const [totalCost, setTotalCost] = useState("0.00");
  const [isManualPrice, setIsManualPrice] = useState(false);
  
  // Portfolio fields
  const [recipePhotoUrl, setRecipePhotoUrl] = useState("");
  const [method, setMethod] = useState("");
  const [copyrightOwner, setCopyrightOwner] = useState("");
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  const currency = settings?.currency || "USD";

  // Auto-fill copyright owner from restaurant name
  useEffect(() => {
    if (settings?.restaurant_name && !copyrightOwner) {
      setCopyrightOwner(settings.restaurant_name);
    }
  }, [settings]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload a valid image file');
      return;
    }

    setUploading(true);
    try {
      // Compress image to WebP format, target <200KB
      const options = {
        maxSizeMB: 0.2, // 200KB
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/webp',
        quality: 0.85
      };
      
      toast.info('Compressing image to WebP...');
      const compressedFile = await imageCompression(file, options);
      
      // Create a new File object with .webp extension
      const webpFile = new File([compressedFile], file.name.replace(/\.[^/.]+$/, '.webp'), {
        type: 'image/webp'
      });
      
      console.log(`Original size: ${(file.size / 1024).toFixed(2)}KB, Compressed: ${(webpFile.size / 1024).toFixed(2)}KB`);
      
      const formData = new FormData();
      formData.append('file', webpFile);

      const response = await axios.post(`${API}/upload/recipe-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const photoUrl = response.data.url;
      setRecipePhotoUrl(photoUrl);
      setPhotoPreview(`${API}${photoUrl}`);
      toast.success(`Photo uploaded! Size: ${(webpFile.size / 1024).toFixed(2)}KB`);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Calculate suggested menu price based on cost, waste %, and target margin
  const calculateSuggestedPrice = (cost, margin, waste = 0) => {
    const costNum = parseFloat(cost) || 0;
    const marginNum = parseFloat(margin) || 0;
    const wasteNum = parseFloat(waste) || 0;
    
    // Apply waste percentage to cost
    const costWithWaste = costNum * (1 + (wasteNum / 100));
    
    if (costWithWaste === 0) return "0.00";
    if (marginNum >= 100) return "0.00"; // Can't have 100% margin
    
    // Formula: Menu Price = (Total Cost + Waste %) / (1 - (Margin % / 100))
    const suggestedPrice = costWithWaste / (1 - (marginNum / 100));
    return suggestedPrice.toFixed(2);
  };

  // Calculate margin from manual menu price and cost (including waste)
  const calculateMarginFromPrice = (price, cost, waste = 0) => {
    const priceNum = parseFloat(price) || 0;
    const costNum = parseFloat(cost) || 0;
    const wasteNum = parseFloat(waste) || 0;
    
    // Apply waste percentage to cost
    const costWithWaste = costNum * (1 + (wasteNum / 100));
    
    if (priceNum === 0) return 0;
    
    // Formula: Margin % = ((Menu Price - (Cost + Waste)) / Menu Price) × 100
    const margin = ((priceNum - costWithWaste) / priceNum) * 100;
    return Math.max(0, Math.min(90, margin)); // Clamp between 0-90%
  };
  
  // Calculate gross profit
  const calculateGrossProfit = () => {
    const price = parseFloat(menuPrice) || 0;
    const cost = parseFloat(totalCost) || 0;
    const waste = parseFloat(wastePercentage) || 0;
    
    const costWithWaste = cost * (1 + (waste / 100));
    const profit = price - costWithWaste;
    
    return {
      profit: profit.toFixed(2),
      costWithWaste: costWithWaste.toFixed(2),
      isProfit: profit > 0
    };
  };

  // Update menu price when cost or target margin changes (if not manual)
  useEffect(() => {
    if (!isManualPrice) {
      const suggested = calculateSuggestedPrice(totalCost, targetMargin);
      setMenuPrice(suggested);
    }
  }, [totalCost, targetMargin, isManualPrice]);

  // Handle margin slider change
  const handleMarginChange = (value) => {
    setTargetMargin(value);
    setIsManualPrice(false); // Switch back to auto mode
  };

  // Handle manual menu price input
  const handleMenuPriceChange = (value) => {
    const normalized = normalizeDecimalInput(value);
    setMenuPrice(normalized);
    setIsManualPrice(true);
    
    // Update slider to reflect the resulting margin
    if (normalized && parseFloat(normalized) > 0) {
      const resultingMargin = calculateMarginFromPrice(normalized, totalCost);
      setTargetMargin(resultingMargin);
    }
  };

  const handleIngredientsChange = (newIngredients) => {
    setRecipeIngredients(newIngredients);
  };

  const handleTotalCostChange = (newTotalCost) => {
    setTotalCost(newTotalCost);
    
    // If manual price mode, recalculate margin
    if (isManualPrice && menuPrice) {
      const resultingMargin = calculateMarginFromPrice(menuPrice, newTotalCost);
      setTargetMargin(resultingMargin);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="recipe-composer">
      <Toaster position="top-right" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white font-manrope flex items-center gap-3">
            <ChefHat className="h-8 w-8 text-amber-500" />
            {t("recipeLab.title")}
          </h1>
          <p className="text-slate-400 mt-2">{t("recipeLab.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Recipe Composition */}
          <div>
            <RecipeCompositionArea
              recipeIngredients={recipeIngredients}
              onIngredientsChange={handleIngredientsChange}
              onTotalCostChange={handleTotalCostChange}
              currency={currency}
            />
          </div>

          {/* Right Column - Pricing */}
          <div className="space-y-6">
            {/* Recipe Name */}
            <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
              <Label htmlFor="recipeName" className="text-slate-300 mb-3 block text-lg">
                {t("recipeModal.name")}
              </Label>
              <Input
                id="recipeName"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                placeholder={t("recipeModal.namePlaceholder")}
                className="bg-slate-800 border-slate-700 text-white text-lg"
                data-testid="recipe-name-input"
              />
            </div>

            {/* Department - Universal Palette */}
            <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
              <Label htmlFor="department" className="text-slate-300 mb-3 block text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5 text-amber-500" />
                Department
              </Label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger 
                  className="bg-slate-800 border-slate-700 text-white text-lg"
                  data-testid="department-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept.value} value={dept.value} className="text-white">
                      {dept.emoji} {dept.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                Organize recipes by department: Kitchen, Bar, Lab, Bakery, or Pastry
              </p>
            </div>
            
            {/* Category - Vijayapuram Menu Organization */}
            <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
              <Label htmlFor="category" className="text-slate-300 mb-3 block text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-500" />
                Category
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger 
                  className="bg-slate-800 border-slate-700 text-white text-lg"
                  data-testid="category-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white">
                      {cat.emoji} {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                Categorize for menu organization: Appetizers, Main Course, Beverages, Desserts, etc.
              </p>
            </div>
            
            {/* Waste Percentage */}
            <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
              <Label htmlFor="wastePercentage" className="text-slate-300 mb-3 block text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                Waste Percentage
              </Label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="30"
                  step="1"
                  value={wastePercentage}
                  onChange={(e) => setWastePercentage(parseFloat(e.target.value))}
                  className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
                />
                <span className="text-2xl font-bold text-orange-400 min-w-[60px] text-right">
                  {wastePercentage}%
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Account for trimming, evaporation, and prep waste in your cost calculation
              </p>
            </div>
            
            {/* MARGIN ENGINE - The Vijayapuram Profit Calculator */}
            <div className="bg-gradient-to-br from-green-900/30 to-slate-900/50 rounded-lg border-2 border-green-500/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  Margin Engine
                </h3>
                <span className="text-xs text-slate-400 px-2 py-1 bg-slate-800 rounded">
                  Live Calculator
                </span>
              </div>
              
              {(() => {
                const { profit, costWithWaste, isProfit } = calculateGrossProfit();
                const marginPercent = parseFloat(menuPrice) > 0 
                  ? (((parseFloat(menuPrice) - parseFloat(costWithWaste)) / parseFloat(menuPrice)) * 100).toFixed(1)
                  : 0;
                
                return (
                  <div className="space-y-4">
                    {/* Formula Display */}
                    <div className="bg-slate-800/50 rounded-lg p-4 text-sm font-mono">
                      <div className="text-slate-400 mb-2">Formula:</div>
                      <div className="text-white">
                        <span className="text-blue-400">{currency} {menuPrice || '0.00'}</span>
                        {' '}-{' '}
                        (<span className="text-red-400">{currency} {totalCost || '0.00'}</span>
                        {' '}+{' '}
                        <span className="text-orange-400">{wastePercentage}%</span>)
                        {' '}={' '}
                        <span className={isProfit ? "text-green-400 font-bold" : "text-red-400 font-bold"}>
                          {currency} {profit}
                        </span>
                      </div>
                    </div>
                    
                    {/* Result Cards */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="text-slate-400 text-xs mb-1">Cost + Waste</div>
                        <div className="text-red-400 text-2xl font-bold">
                          {currency} {costWithWaste}
                        </div>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="text-slate-400 text-xs mb-1">Gross Profit</div>
                        <div className={`text-2xl font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                          {currency} {profit}
                        </div>
                      </div>
                    </div>
                    
                    {/* Margin Percentage Badge */}
                    <div className={`text-center py-3 rounded-lg ${
                      parseFloat(marginPercent) >= 60 ? 'bg-green-500/20 border border-green-500' :
                      parseFloat(marginPercent) >= 40 ? 'bg-yellow-500/20 border border-yellow-500' :
                      'bg-red-500/20 border border-red-500'
                    }`}>
                      <div className="text-xs text-slate-300 mb-1">Current Margin</div>
                      <div className={`text-3xl font-bold ${
                        parseFloat(marginPercent) >= 60 ? 'text-green-400' :
                        parseFloat(marginPercent) >= 40 ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {marginPercent}%
                      </div>
                    </div>
                    
                    {/* Status Message */}
                    {!isProfit && parseFloat(menuPrice) > 0 && (
                      <div className="bg-red-500/10 border border-red-500 rounded-lg p-3 text-sm text-red-400">
                        ⚠️ Warning: This recipe is operating at a loss. Increase menu price or reduce costs.
                      </div>
                    )}
                    {isProfit && parseFloat(marginPercent) < 40 && (
                      <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-3 text-sm text-yellow-400">
                        💡 Tip: Margin below 40%. Consider optimizing recipe or adjusting price.
                      </div>
                    )}
                    {parseFloat(marginPercent) >= 60 && (
                      <div className="bg-green-500/10 border border-green-500 rounded-lg p-3 text-sm text-green-400">
                        ✅ Excellent! This recipe maintains a healthy profit margin.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Margin Slider Input - Bottom-Up Pricing */}
            <MarginSliderInput
              targetMargin={targetMargin}
              onMarginChange={handleMarginChange}
              totalCost={totalCost}
              menuPrice={menuPrice}
              currency={currency}
              isManualPrice={isManualPrice}
            />

            {/* Manual Menu Price Override */}
            <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
              <Label htmlFor="menuPrice" className="text-slate-300 mb-3 block text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-500" />
                {t("recipeModal.menuPrice", { currency })} (Manual Override)
              </Label>
              <Input
                id="menuPrice"
                type="number"
                step="0.01"
                value={menuPrice}
                onChange={(e) => handleMenuPriceChange(e.target.value)}
                placeholder={t("recipeModal.menuPricePlaceholder")}
                className="bg-slate-800 border-slate-700 text-white text-lg font-mono"
                data-testid="menu-price-input"
              />
              <p className="text-xs text-slate-500 mt-2">
                Leave blank to use suggested price, or enter a custom price
              </p>
            </div>

            {/* Visual Craft vs. Margin Slider */}
            <CraftMarginSlider
              totalCost={totalCost}
              menuPrice={menuPrice}
              currency={currency}
            />
          </div>
        </div>

        {/* Portfolio & Branding Section */}
        <div className="mt-8 bg-slate-900/50 rounded-lg border border-slate-800 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Award className="h-6 w-6 text-amber-500" />
            {t("recipeComposer.portfolioSection")}
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recipe Photo Upload */}
            <div>
              <Label className="text-lg font-semibold text-white mb-1 block">
                <Camera className="inline h-5 w-5 mr-2 text-amber-500" />
                {t("recipeComposer.recipePhoto")}
              </Label>
              <p className="text-sm text-slate-400 mb-4">{t("recipeComposer.photoDesc")}</p>
              <div className="flex items-center gap-4">
                {photoPreview && (
                  <div className="w-32 h-32 rounded-lg overflow-hidden border-2 border-amber-500/50 bg-slate-800">
                    <img src={photoPreview} alt="Recipe" className="w-full h-full object-cover" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <div className="px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg transition-colors inline-block">
                    {uploading ? t('recipeComposer.uploading') : t('recipeComposer.uploadPhoto')}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {t('recipeComposer.photoHint')}
              </p>
            </div>

            {/* Copyright Owner */}
            <div>
              <Label htmlFor="copyright_owner" className="text-lg font-semibold text-white mb-1 block">
                {t("recipeComposer.copyrightOwner")}
              </Label>
              <p className="text-sm text-slate-400 mb-4">{t("recipeComposer.copyrightDesc")}</p>
              <Input
                id="copyright_owner"
                type="text"
                value={copyrightOwner}
                onChange={(e) => setCopyrightOwner(e.target.value)}
                placeholder={t("recipeComposer.copyrightPlaceholder")}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="copyright-owner-input"
              />
              <p className="text-xs text-slate-500 mt-2">
                {t('recipeComposer.copyrightHint')}
              </p>
            </div>
          </div>

          {/* Cooking Method */}
          <div className="mt-6">
            <Label htmlFor="method" className="text-lg font-semibold text-white mb-1 block">
              <FileText className="inline h-5 w-5 mr-2 text-amber-500" />
              {t("recipeComposer.method")}
            </Label>
            <p className="text-sm text-slate-400 mb-4">{t("recipeComposer.methodDesc")}</p>
            <Textarea
              id="method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              rows={6}
              className="bg-slate-800 border-slate-700 text-white resize-none"
              placeholder={t("recipeComposer.methodPlaceholder")}
              data-testid="method-textarea"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end gap-4">
          <Button
            variant="outline"
            className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
          >
            {t("common.cancel")}
          </Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-8"
            disabled={!recipeName || !menuPrice || recipeIngredients.length === 0}
            data-testid="save-recipe-button"
          >
            {t("recipeModal.createRecipe")}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default RecipeComposer;
